import { useEffect, useState } from "react";
import { base44 } from "@/api/base44Client";
import { AD_SLOT } from "@/data/ads";
import AdCreative from "@/components/yts/AdCreative";
import ExperienceAd from "@/components/yts/ads/ExperienceAd";

/**
 * One sponsor space on a page. A space a partner has paid for shows their own
 * artwork; an unsold one shows the house card that sells it, in the one shape
 * every space on the site shares (see ads/HouseAd.jsx).
 *
 * The placement itself (src/data/ads.js) supplies the size line and decides
 * how much room the card is given, so the same component serves a band between
 * the home page's rows, a card inside a rail, a strip under a business's
 * details and a slot pinned to the map panel.
 */
// The landing page carries a band under every row, so the same placement is
// asked for several times on one page: one lookup per placement, shared by
// every slot that wants it.
const lookups = new Map();

function findCampaign(placement) {
  if (!lookups.has(placement)) {
    lookups.set(
      placement,
      base44.entities.AdCampaign
        .filter(
          {
            placement_id: placement,
            status: "Active",
            starts_at: { $lte: new Date().toISOString() },
            ends_at: { $gte: new Date().toISOString() },
          },
          { sort: "-paid_at", limit: 1 }
        )
        .then((page) => page?.items?.[0] || null)
        .catch(() => null)
    );
  }
  return lookups.get(placement);
}

export default function AdSlot({ placement, slot }) {
  const ad = AD_SLOT[placement];
  // Undefined while the space is being checked.
  const [sold, setSold] = useState(undefined);

  useEffect(() => {
    if (!ad?.variant) return undefined;
    let live = true;
    findCampaign(placement).then((campaign) => live && setSold(campaign));
    return () => {
      live = false;
    };
  }, [placement, ad?.variant]);

  if (!ad) return null;
  // The panel slot is a third of a band wide, so its card drops the blurb and
  // the wider photo plate to stay readable.
  const compact = placement === "map-panel";
  if (sold) return <AdCreative campaign={sold} compact={compact} />;
  // One experience banner for every unsold space on the site: the room it is
  // given changes, the banner does not (see ads/ExperienceAd.jsx).
  return <ExperienceAd placement={placement} slot={slot} compact={compact} />;
}